import * as React from 'react';
import {Box, Typography, Tooltip} from '@mui/material';
import DelayMisDownloadButton from '../ct/DelayMisDownloadButton';
import useAddressesByAwb from '../../common/hooks/useAddressesByAwb';
import _ from 'lodash';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import CurrencyRupeeOutlinedIcon from '@mui/icons-material/CurrencyRupeeOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import {formatCurrency, parseDateAndTime} from "../../helpers/UtilityHelper";
import {primaryTextSx, secondaryTextSx, tertiaryTextSx, awbTextSx, cellStackSx} from '../orders/list2/cellStyles';
import AwbLinkCell from '../../common/AwbLinkCell';
import Loader from '../../common/Loader';
import useLostAndDamaged from '../../common/hooks/useLostAndDamaged';
import {getMerchant} from '../../helpers/ReduxHelpers';
import ProzoDataGrid from '../../common/ProzoDataGrid';
import DashboardDateFilter, {DateRangeCaption} from '../../common/DashboardDateFilter';
import {subDays} from 'date-fns';
import {formatDate} from '../../helpers/UtilityHelper';
import {FONT, SIZE, WEIGHT} from '../../styles/foundation/typography';
import {NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, ERROR, WARNING, RED, withOpacity} from '../../styles/foundation/colors';
import {SHADOW} from '../../styles/foundation/effects';

const sectionCardSx = {
    bgcolor: WHITE, borderRadius: '10px', boxShadow: SHADOW.card,
    border: `1px solid ${BORDER}`, overflow: 'hidden', mb: 2.5,
};
// One colour per courier / zone slice in the lost-shipment donuts.
const LOST_COLORS = ['#c0392b', '#d99a3a', '#3783e7', '#2e9e6b', '#7a4fb0', '#0f766e', '#8f2f2c'];
// Cockpit section header — uppercase, ruled (matches the Shipments tab).
const sectionTitleSx = {
    fontFamily: FONT, fontSize: '11px', fontWeight: WEIGHT.extra, letterSpacing: '0.07em',
    textTransform: 'uppercase', color: SUBTLE, px: 2, py: 1.5, borderBottom: `1px solid ${BORDER}`,
};

export default function Lost({shipType}) {
    const [dateRange, setDateRange] = React.useState([{
        endDate: subDays(new Date(), 1), startDate: subDays(new Date(), 90), key: 'selection'
    }])
    const {fromDate: from_date, toDate: to_date} = {
        fromDate: formatDate(dateRange[0].startDate, 'yyyy-MM-dd'),
        toDate: formatDate(dateRange[0].endDate, 'yyyy-MM-dd')
    }
    const merchant = getMerchant();
    const {isCalculating, data: lostOrders} = useLostAndDamaged(merchant.id, from_date, to_date, shipType);
    // Enrich by AWB so the Courier column can show "parent — service type" (lost counts are
    // small, so we enrich the whole list rather than page-by-page).
    const {addrMap} = useAddressesByAwb((lostOrders || []).map((r) => r.awbNumber));
    const noDeliveryOrders = _.filter(lostOrders, (item) => item.deliveryAttemptCount === 0 || _.isNil(item.deliveryAttemptCount));

    const courierData = _.chain(lostOrders)
        .groupBy("courierName")
        .map((orders, courierName) => ({courierName, totalCount: orders.length, totalInvoiceValue: _.sumBy(orders, "invoiceValue")}))
        .value();

    const zoneData = _.chain(lostOrders)
        .groupBy("merchantZone")
        .map((orders, merchantZone) => ({merchantZone, totalCount: orders.length, totalInvoiceValue: _.sumBy(orders, "invoiceValue")}))
        .value();

    const [sizePerPage1, setSizePerPage1] = React.useState(30);
    const [page, setPage] = React.useState(0);
    // Merged Courier/Zone breakdown — one card, toggled by dimension.
    const [breakdownDim, setBreakdownDim] = React.useState('courier');

    // Ring data for the merged breakdown: count, lost value and % share per courier / zone.
    const totalLostCount = lostOrders?.length || 0;
    const buildRings = (rows, key) => _.orderBy(rows, 'totalCount', 'desc').map((r, i) => ({
        name: (r[key] && r[key] !== 'undefined') ? r[key] : 'Unknown',
        count: r.totalCount, value: r.totalInvoiceValue,
        pct: totalLostCount ? Math.round((r.totalCount / totalLostCount) * 100) : 0,
        color: LOST_COLORS[i % LOST_COLORS.length],
    }));
    const breakdownRings = breakdownDim === 'courier' ? buildRings(courierData, 'courierName') : buildRings(zoneData, 'merchantZone');
    // conic-gradient stops for the donut (precise count share, in ranked order).
    let _acc = 0;
    const donutStops = breakdownRings.map((r) => {
        const from = _acc;
        _acc += totalLostCount ? (r.count / totalLostCount) * 100 : 0;
        return `${r.color} ${from.toFixed(2)}% ${_acc.toFixed(2)}%`;
    }).join(', ');

    const totalLostValue = Math.round(_.sumBy(lostOrders, (item) => parseFloat(item.invoiceValue)));

    // Bulk MIS of all lost shipments in the range. Uses the shared delay-MIS exporter so the
    // file carries the same standard columns as the Shipment-Alert downloads — Reference,
    // Courier, AWB, dates, Zone, and (enriched by AWB) Order Status, Pickup + Drop
    // name/city/state/pincode and Invoice Value.
    const lostBaseColumns = [
        {label: 'Reference', value: (r) => r.reference || r.shippingLabelId || ''},
        {label: 'Courier Partner', value: 'courierName'},
        {label: 'AWB Number', value: 'awbNumber'},
        {label: 'Order Date', value: (r) => (r.date ? parseDateAndTime(r.date) : '')},
        {label: 'Lost Date', value: (r) => (r.lostDamagedTimestamp ? parseDateAndTime(r.lostDamagedTimestamp) : '')},
        {label: 'Zone', value: 'merchantZone'},
        {label: 'Delivery Attempts', value: (r) => (r.deliveryAttemptCount ?? '')},
    ];

    const kpis = [
        {label: 'Total Lost Shipments', value: lostOrders?.length || 0, icon: <ReportProblemOutlinedIcon />, color: ERROR},
        {label: 'Lost Shipment Value', value: formatCurrency(totalLostValue), icon: <CurrencyRupeeOutlinedIcon />, color: WARNING},
        {label: 'Lost Before Delivery', value: noDeliveryOrders.length, icon: <BlockOutlinedIcon />, color: RED},
    ];

    const allShipmentsColumns = [
        {field: 'shipment', headerName: 'Shipment', flex: 1.6, minWidth: 190, sortable: false, hideSortIcons: true, headerAlign: 'left', renderCell: ({row}) => (
            <Box sx={cellStackSx}>
                <AwbLinkCell awb={row.awbNumber} sx={awbTextSx} />
                {row.reference && <Typography sx={secondaryTextSx}>{row.reference}</Typography>}
                <Typography sx={secondaryTextSx}>{row.date ? parseDateAndTime(row.date) : '—'}</Typography>
            </Box>
        )},
        {field: 'courierName', headerName: 'Courier', flex: 1, minWidth: 130, sortable: false, hideSortIcons: true, headerAlign: 'left', renderCell: ({row}) => (
            <Typography sx={primaryTextSx}>{addrMap[row.awbNumber]?.courierLabel || row.courierName || '—'}</Typography>
        )},
        {field: 'zone', headerName: 'Zone & Delivery', flex: 1.3, minWidth: 160, sortable: false, hideSortIcons: true, headerAlign: 'left', renderCell: ({row}) => (
            <Box sx={cellStackSx}>
                <Typography sx={primaryTextSx}>{row.merchantZone || '—'}</Typography>
                <Typography sx={secondaryTextSx}>Delivery PIN: {row.dropPincode || '—'}</Typography>
            </Box>
        )},
        {field: 'lostDamagedTimestamp', headerName: 'Lost Date', flex: 1.2, minWidth: 150, sortable: false, hideSortIcons: true, headerAlign: 'left', renderCell: ({row}) => (
            <Box sx={cellStackSx}>
                <Typography sx={{...primaryTextSx, color: ERROR}}>{row.lostDamagedTimestamp ? parseDateAndTime(row.lostDamagedTimestamp) : '—'}</Typography>
                {row.deliveryAttemptCount != null && <Typography sx={tertiaryTextSx}>{row.deliveryAttemptCount} delivery attempts</Typography>}
            </Box>
        )},
        {field: 'invoiceValue', headerName: 'Invoice', flex: 0.7, minWidth: 100, align: 'right', headerAlign: 'right', sortable: false, hideSortIcons: true, renderCell: ({row}) => (
            <Typography sx={primaryTextSx}>₹{Math.round(row.invoiceValue || 0)}</Typography>
        )},
    ];

    return (
        <>
            {isCalculating && <Loader />}

            {/* Date range filter — kept on top so the active range is visible at a glance */}
            <Box sx={{
                bgcolor: WHITE, borderRadius: '10px', boxShadow: SHADOW.card, border: `1px solid ${BORDER}`,
                px: 2.5, py: 1.5, mb: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5,
            }}>
                <DateRangeCaption dateRange={dateRange} />
                <DashboardDateFilter dateRange={dateRange} maxDate={subDays(new Date(), 1)} onChange={(range) => setDateRange(range)} />
            </Box>

            {/* KPI cards — cockpit metric strip (matches the Shipments tab) */}
            <Box sx={{
                display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(3,1fr)'},
                bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '10px', overflow: 'hidden',
                boxShadow: SHADOW.card, mb: '20px',
            }}>
                {kpis.map((k) => (
                    <Box key={k.label} sx={{p: 1.75, borderRight: `1px solid ${BORDER}`, '&:last-of-type': {borderRight: 'none'}}}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                            <Box sx={{width: 7, height: 7, borderRadius: '2px', bgcolor: k.color, flex: 'none'}} />
                            <Typography sx={{fontSize: '9.5px', fontWeight: WEIGHT.extra, letterSpacing: '0.05em', textTransform: 'uppercase', color: SUBTLE, fontFamily: FONT}}>{k.label}</Typography>
                        </Box>
                        <Typography sx={{fontSize: '22px', fontWeight: WEIGHT.extra, lineHeight: 1, color: k.color, fontFamily: FONT, fontVariantNumeric: 'tabular-nums', mt: 0.75}}>
                            {k.value}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* How-it's-counted callout — modern info banner (accent bar + icon chip) */}
            <Box sx={{
                display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 2.5,
                bgcolor: withOpacity(PRIMARY_BLUE, 0.05), border: `1px solid ${withOpacity(PRIMARY_BLUE, 0.22)}`,
                borderLeft: `3px solid ${PRIMARY_BLUE}`, borderRadius: '10px', px: 2, py: 1.5,
            }}>
                <Box sx={{width: 28, height: 28, borderRadius: '8px', bgcolor: withOpacity(PRIMARY_BLUE, 0.12), display: 'grid', placeItems: 'center', flex: 'none', mt: 0.2}}>
                    <InfoOutlinedIcon sx={{fontSize: 17, color: PRIMARY_BLUE}} />
                </Box>
                <Box>
                    <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, fontWeight: WEIGHT.bold, color: NAVY, mb: 0.4}}>
                        How these numbers are counted
                    </Typography>
                    <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, lineHeight: 1.55}}>
                        Shipments <Box component='span' sx={{fontWeight: WEIGHT.bold, color: NAVY}}>marked lost</Box> in the selected{' '}
                        <Box component='span' sx={{fontWeight: WEIGHT.semibold, color: NAVY}}>{Math.round((new Date(to_date) - new Date(from_date)) / 86400000)}-day</Box> window —
                        counted by the <Box component='span' sx={{fontWeight: WEIGHT.semibold, color: NAVY}}>lost-marked date</Box>, not the order-created date.{' '}
                        <Box component='span' sx={{fontWeight: WEIGHT.bold, color: RED}}>Lost Before Delivery</Box> = lost with zero delivery attempts, so investigate those first.
                    </Typography>
                </Box>
            </Box>

            {lostOrders?.length > 0 && (
                <>
                    {/* Merged Courier / Zone breakdown — ring per item, toggled by dimension */}
                    <Box sx={sectionCardSx}>
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: `1px solid ${BORDER}`, flexWrap: 'wrap', gap: 1}}>
                            <Typography sx={{...sectionTitleSx, px: 0, py: 0, borderBottom: 'none'}}>
                                Lost Shipments by {breakdownDim === 'courier' ? 'Courier' : 'Zone'}
                            </Typography>
                            <Box sx={{display: 'flex', bgcolor: '#f1f4f8', borderRadius: '8px', p: '3px'}}>
                                {[{v: 'courier', l: 'Courier'}, {v: 'zone', l: 'Zone'}].map((o) => (
                                    <Box key={o.v} onClick={() => setBreakdownDim(o.v)} sx={{
                                        fontFamily: FONT, fontSize: '12px', fontWeight: WEIGHT.bold, px: 1.75, py: 0.5, borderRadius: '6px', cursor: 'pointer',
                                        color: breakdownDim === o.v ? NAVY : SUBTLE, bgcolor: breakdownDim === o.v ? WHITE : 'transparent',
                                        boxShadow: breakdownDim === o.v ? SHADOW.card : 'none',
                                    }}>{o.l}</Box>
                                ))}
                            </Box>
                        </Box>
                        <Box sx={{p: 3}}>
                            {breakdownRings.length === 0 ? (
                                <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: SUBTLE, py: 2}}>No lost shipments in this range.</Typography>
                            ) : (
                                <Box sx={{display: 'flex', alignItems: 'center', gap: {xs: 3, md: 5}, flexWrap: 'wrap', justifyContent: 'center'}}>
                                    {/* Donut */}
                                    <Box sx={{
                                        width: 196, height: 196, borderRadius: '50%', flex: 'none', display: 'grid', placeItems: 'center', position: 'relative',
                                        background: `conic-gradient(${donutStops})`,
                                        '&::after': {content: '""', position: 'absolute', width: 124, height: 124, bgcolor: WHITE, borderRadius: '50%'},
                                    }}>
                                        <Box sx={{position: 'relative', zIndex: 1, textAlign: 'center'}}>
                                            <Typography sx={{fontFamily: FONT, fontSize: '30px', fontWeight: WEIGHT.extra, color: ERROR, lineHeight: 1}}>{totalLostCount.toLocaleString()}</Typography>
                                            <Typography sx={{fontFamily: FONT, fontSize: '10px', color: SUBTLE, textTransform: 'uppercase', letterSpacing: '0.06em'}}>Lost Shipments</Typography>
                                        </Box>
                                    </Box>
                                    {/* Legend — fills the rest of the block */}
                                    <Box sx={{flex: 1, minWidth: 300, display: 'flex', flexDirection: 'column', gap: 1.4}}>
                                        {breakdownRings.map((r) => (
                                            <Tooltip key={r.name} arrow title={`${r.name}: ${r.count.toLocaleString()} lost (${r.pct}%) · ${formatCurrency(Math.round(r.value || 0))}`}>
                                                <Box sx={{display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 1.25, alignItems: 'center', cursor: 'default'}}>
                                                    <Box sx={{width: 11, height: 11, borderRadius: '3px', bgcolor: r.color, flex: 'none'}} />
                                                    <Box sx={{minWidth: 0}}>
                                                        <Typography sx={{fontFamily: FONT, fontSize: '12.5px', fontWeight: WEIGHT.semibold, color: NAVY, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{r.name}</Typography>
                                                        <Box sx={{height: 5, bgcolor: '#f1f4f8', borderRadius: '3px', overflow: 'hidden', mt: 0.4}}>
                                                            <Box sx={{height: '100%', width: `${r.pct}%`, minWidth: 3, bgcolor: r.color, borderRadius: '3px'}} />
                                                        </Box>
                                                    </Box>
                                                    <Box sx={{textAlign: 'right', whiteSpace: 'nowrap'}}>
                                                        <Typography sx={{fontFamily: FONT, fontSize: '12.5px', color: SUBTLE, fontVariantNumeric: 'tabular-nums'}}>
                                                            <Box component='span' sx={{color: NAVY, fontWeight: WEIGHT.bold}}>{r.count.toLocaleString()}</Box> ({r.pct}%)
                                                        </Typography>
                                                        <Typography sx={{fontFamily: FONT, fontSize: '10.5px', color: SUBTLE, fontVariantNumeric: 'tabular-nums'}}>{formatCurrency(Math.round(r.value || 0))}</Typography>
                                                    </Box>
                                                </Box>
                                            </Tooltip>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* All shipments table */}
                    <Box sx={sectionCardSx}>
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: `1px solid ${BORDER}`}}>
                            <Typography sx={{...sectionTitleSx, px: 0, py: 0, borderBottom: 'none'}}>All Lost Shipments</Typography>
                            <DelayMisDownloadButton
                                rows={lostOrders || []}
                                filename="Lost and Damaged"
                                label="Download MIS"
                                baseColumns={lostBaseColumns}
                            />
                        </Box>
                        <Box sx={{p: 1}}>
                            <ProzoDataGrid columns={allShipmentsColumns} autoHeight rows={lostOrders || []}
                                sizePerPage={sizePerPage1} setSizePerPage={setSizePerPage1} setPage={setPage} page={page}
                                pagination hideFooterPagination={false} paginationMode="client"
                                rowCount={lostOrders?.length || 0} getRowId={(row) => row.awbNumber} />
                        </Box>
                    </Box>
                </>
            )}
        </>
    )
}
